
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/components/Auth/AuthProvider';
import { Bell, Mail, Save, Plus, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { NotificationSystem } from './NotificationSystem';
import { supabase } from '@/integrations/supabase/client';

interface NotificationPreferences {
  id?: string;
  user_id: string;
  email_notifications: boolean;
  push_notifications: boolean;
  report_reminders: boolean;
  data_validation_alerts: boolean;
  target_deadline_alerts: boolean;
  assessment_due_alerts: boolean;
  threshold_breach_alerts: boolean;
  weekly_summary: boolean;
  monthly_reports: boolean;
  notification_frequency: string;
  frequency: string;
  quiet_hours_start: string;
  quiet_hours_end: string;
  additional_emails: string[];
  alert_types: Record<string, boolean>;
  periodic_reports: Record<string, boolean>;
  created_at?: string;
  updated_at?: string;
}

const getDefaultPreferences = (userId: string): NotificationPreferences => ({
  user_id: userId,
  email_notifications: true,
  push_notifications: false,
  report_reminders: true,
  data_validation_alerts: true,
  target_deadline_alerts: true,
  assessment_due_alerts: true,
  threshold_breach_alerts: true,
  weekly_summary: true,
  monthly_reports: true,
  notification_frequency: 'immediate',
  frequency: 'immediate',
  quiet_hours_start: '22:00',
  quiet_hours_end: '08:00',
  additional_emails: [],
  alert_types: {
    report_reminders: true,
    data_validation_alerts: true,
    target_deadline_alerts: true,
    assessment_due_alerts: true,
    threshold_breach_alerts: true,
  },
  periodic_reports: {
    weekly_summary: true,
    monthly_reports: true,
  },
});

export const NotificationSettings = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [hasChanges, setHasChanges] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);

  useEffect(() => {
    if (user?.id) {
      loadPreferences();
    }
  }, [user?.id]);

  const loadPreferences = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading preferences:', error);
        toast({
          title: "Error",
          description: "Failed to load notification preferences.",
          variant: "destructive",
        });
        // Set default preferences even if there's an error
        setPreferences(getDefaultPreferences(user.id));
        return;
      }

      if (data) {
        // Safely parse JSON fields with fallbacks
        const alertTypes = typeof data.alert_types === 'object' && data.alert_types !== null 
          ? data.alert_types as Record<string, boolean>
          : {
              report_reminders: data.report_reminders ?? true,
              data_validation_alerts: data.data_validation_alerts ?? true,
              target_deadline_alerts: data.target_deadline_alerts ?? true,
              assessment_due_alerts: data.assessment_due_alerts ?? true,
              threshold_breach_alerts: data.threshold_breach_alerts ?? true,
            };

        const periodicReports = typeof data.periodic_reports === 'object' && data.periodic_reports !== null
          ? data.periodic_reports as Record<string, boolean>
          : {
              weekly_summary: data.weekly_summary ?? true,
              monthly_reports: data.monthly_reports ?? true,
            };

        const loadedPreferences: NotificationPreferences = {
          id: data.id,
          user_id: data.user_id,
          email_notifications: data.email_notifications ?? true,
          push_notifications: data.push_notifications ?? false,
          report_reminders: data.report_reminders ?? true,
          data_validation_alerts: data.data_validation_alerts ?? true,
          target_deadline_alerts: data.target_deadline_alerts ?? true,
          assessment_due_alerts: data.assessment_due_alerts ?? true,
          threshold_breach_alerts: data.threshold_breach_alerts ?? true,
          weekly_summary: data.weekly_summary ?? true,
          monthly_reports: data.monthly_reports ?? true,
          notification_frequency: data.notification_frequency || data.frequency || 'immediate',
          frequency: data.frequency || data.notification_frequency || 'immediate',
          quiet_hours_start: data.quiet_hours_start || '22:00',
          quiet_hours_end: data.quiet_hours_end || '08:00',
          additional_emails: data.additional_emails || [],
          alert_types: alertTypes,
          periodic_reports: periodicReports,
          created_at: data.created_at,
          updated_at: data.updated_at,
        };

        setPreferences(loadedPreferences);
        console.log('Loaded preferences:', loadedPreferences);
      } else {
        // No existing preferences, set defaults
        const defaultPrefs = getDefaultPreferences(user.id);
        setPreferences(defaultPrefs);
        console.log('No existing preferences found, using defaults:', defaultPrefs);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      // Set default preferences on error
      setPreferences(getDefaultPreferences(user.id));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (sendTest = false) => {
    if (!user?.id || !preferences) {
      toast({
        title: "Error",
        description: "User not authenticated or preferences not loaded.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      // Prepare alert types and periodic reports
      const alertTypes = {
        report_reminders: preferences.report_reminders,
        data_validation_alerts: preferences.data_validation_alerts,
        target_deadline_alerts: preferences.target_deadline_alerts,
        assessment_due_alerts: preferences.assessment_due_alerts,
        threshold_breach_alerts: preferences.threshold_breach_alerts,
      };

      const periodicReports = {
        weekly_summary: preferences.weekly_summary,
        monthly_reports: preferences.monthly_reports,
      };

      const payload = {
        userId: user.id,
        emailNotifications: preferences.email_notifications,
        pushNotifications: preferences.push_notifications,
        frequency: preferences.frequency,
        alertTypes,
        periodicReports,
        quietHours: {
          start: preferences.quiet_hours_start,
          end: preferences.quiet_hours_end,
        },
        additionalEmails: preferences.additional_emails,
        sendTest,
      };

      console.log('Saving preferences with payload:', payload);

      const { data, error } = await supabase.functions.invoke('notification-preferences', {
        body: payload,
      });

      if (error) {
        console.error('Function invoke error:', error);
        throw error;
      }

      if (data?.success === false) {
        throw new Error(data.error || 'Failed to save preferences');
      }

      toast({
        title: sendTest ? "Test Email Sent" : "Preferences Saved",
        description: sendTest 
          ? "Check your inbox for the test notification."
          : "Your notification preferences have been saved successfully.",
      });
      
      setHasChanges(false);

      // Reload preferences to get the latest data
      await loadPreferences();
    } catch (error: any) {
      console.error('Error saving preferences:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save notification preferences.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const sendTestNotification = () => {
    handleSave(true);
  };

  const handlePreferenceChange = (key: keyof NotificationPreferences, value: any) => {
    if (!preferences) return;
    
    console.log(`Changing ${key} to:`, value);
    setPreferences(prev => {
      if (!prev) return prev;
      
      const updated = { ...prev, [key]: value };
      
      // Update alert_types and periodic_reports when individual toggles change
      if (key === 'report_reminders' || key === 'data_validation_alerts' || 
          key === 'target_deadline_alerts' || key === 'assessment_due_alerts' || 
          key === 'threshold_breach_alerts') {
        updated.alert_types = {
          ...updated.alert_types,
          [key]: value
        };
      }
      
      if (key === 'weekly_summary' || key === 'monthly_reports') {
        updated.periodic_reports = {
          ...updated.periodic_reports,
          [key]: value
        };
      }
      
      return updated;
    });
    setHasChanges(true);
  };

  const addEmail = () => {
    if (!preferences || !newEmail || preferences.additional_emails.includes(newEmail)) return;
    
    handlePreferenceChange('additional_emails', [...preferences.additional_emails, newEmail]);
    setNewEmail('');
  };

  const removeEmail = (email: string) => {
    if (!preferences) return;
    
    handlePreferenceChange('additional_emails', preferences.additional_emails.filter(e => e !== email));
  };

  if (isLoading || !preferences) {
    return <div className="flex justify-center p-8">Loading notification settings...</div>;
  }

  return (
    <div className="space-y-6">
      <NotificationSystem />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Preferences
          </CardTitle>
          <p className="text-sm text-gray-600">
            Configure how and when you receive notifications about your ESG data and activities.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* General Settings */}
          <div>
            <h3 className="text-lg font-medium mb-4">General Settings</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="email_notifications">Email Notifications</Label>
                  <p className="text-sm text-gray-600">Receive notifications via email</p>
                </div>
                <Switch
                  id="email_notifications"
                  checked={preferences.email_notifications}
                  onCheckedChange={(checked) => handlePreferenceChange('email_notifications', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="push_notifications">Push Notifications</Label>
                  <p className="text-sm text-gray-600">Receive browser push notifications</p>
                </div>
                <Switch
                  id="push_notifications"
                  checked={preferences.push_notifications}
                  onCheckedChange={(checked) => handlePreferenceChange('push_notifications', checked)}
                />
              </div>

              <div>
                <Label htmlFor="frequency">Notification Frequency</Label>
                <Select
                  value={preferences.frequency}
                  onValueChange={(value) => handlePreferenceChange('frequency', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">Immediate</SelectItem>
                    <SelectItem value="daily">Daily Digest</SelectItem>
                    <SelectItem value="weekly">Weekly Summary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Alert Types */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium mb-4">Alert Types</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Report Reminders</Label>
                  <p className="text-sm text-gray-600">Reminders for upcoming report deadlines</p>
                </div>
                <Switch
                  checked={preferences.report_reminders}
                  onCheckedChange={(checked) => handlePreferenceChange('report_reminders', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Data Validation Alerts</Label>
                  <p className="text-sm text-gray-600">Alerts when data validation fails</p>
                </div>
                <Switch
                  checked={preferences.data_validation_alerts}
                  onCheckedChange={(checked) => handlePreferenceChange('data_validation_alerts', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Target Deadline Alerts</Label>
                  <p className="text-sm text-gray-600">Alerts for approaching net-zero target deadlines</p>
                </div>
                <Switch
                  checked={preferences.target_deadline_alerts}
                  onCheckedChange={(checked) => handlePreferenceChange('target_deadline_alerts', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Assessment Due Alerts</Label>
                  <p className="text-sm text-gray-600">Alerts for due supplier assessments</p>
                </div>
                <Switch
                  checked={preferences.assessment_due_alerts}
                  onCheckedChange={(checked) => handlePreferenceChange('assessment_due_alerts', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Threshold Breach Alerts</Label>
                  <p className="text-sm text-gray-600">Alerts when KPIs exceed set thresholds</p>
                </div>
                <Switch
                  checked={preferences.threshold_breach_alerts}
                  onCheckedChange={(checked) => handlePreferenceChange('threshold_breach_alerts', checked)}
                />
              </div>
            </div>
          </div>

          {/* Periodic Reports */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium mb-4">Periodic Reports</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Weekly Summary</Label>
                  <p className="text-sm text-gray-600">Weekly summary of activities and progress</p>
                </div>
                <Switch
                  checked={preferences.weekly_summary}
                  onCheckedChange={(checked) => handlePreferenceChange('weekly_summary', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Monthly Reports</Label>
                  <p className="text-sm text-gray-600">Comprehensive monthly performance reports</p>
                </div>
                <Switch
                  checked={preferences.monthly_reports}
                  onCheckedChange={(checked) => handlePreferenceChange('monthly_reports', checked)}
                />
              </div>
            </div>
          </div>

          {/* Quiet Hours */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium mb-4">Quiet Hours</h3>
            <p className="text-sm text-gray-600 mb-4">
              Set hours when you don't want to receive notifications (except critical alerts)
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quiet_hours_start">Start Time</Label>
                <Input
                  id="quiet_hours_start"
                  type="time"
                  value={preferences.quiet_hours_start}
                  onChange={(e) => handlePreferenceChange('quiet_hours_start', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="quiet_hours_end">End Time</Label>
                <Input
                  id="quiet_hours_end"
                  type="time"
                  value={preferences.quiet_hours_end}
                  onChange={(e) => handlePreferenceChange('quiet_hours_end', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Additional Email Recipients */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Additional Email Recipients
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Add team members who should also receive notifications
            </p>
            
            <div className="flex gap-2 mb-4">
              <Input
                type="email"
                placeholder="Enter email address"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addEmail()}
              />
              <Button onClick={addEmail} variant="outline" size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              {preferences.additional_emails.map((email) => (
                <Badge key={email} variant="secondary" className="flex items-center gap-1">
                  {email}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 hover:bg-transparent"
                    onClick={() => removeEmail(email)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            </div>
          </div>

          {/* Test & Save */}
          <div className="flex justify-between items-center pt-6 border-t">
            <Button
              variant="outline"
              onClick={sendTestNotification}
              disabled={isSaving || !preferences.email_notifications}
              className="flex items-center gap-2"
            >
              <Mail className="h-4 w-4" />
              Send Test Email
            </Button>
            
            <Button 
              onClick={() => handleSave(false)} 
              disabled={isSaving}
              className="flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save Preferences'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
